import {
  Box,
  BoxProps,
  ButtonGroup,
  Divider,
  Flex,
  Heading,
  useColorModeValue,
} from '@chakra-ui/react';
import { forwardRef } from 'react';

type PanelProps = BoxProps;

export function Panel(props: PanelProps): React.ReactElement {
  const backgroundColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  return (
    <Box
      backgroundColor={backgroundColor}
      paddingTop={2}
      paddingX={2}
      borderRadius="2xl"
      borderColor={borderColor}
      borderWidth="1px"
      {...props}
    />
  );
}

type PanelHeaderProps = BoxProps & { hasNoMarginBottom?: boolean };

export function PanelHeader({
  hasNoMarginBottom: hasMarginBottom,
  ...rest
}: PanelHeaderProps): React.ReactElement {
  return (
    <>
      <Flex
        paddingLeft={2}
        justifyContent="space-between"
        alignItems="flex-end"
        minHeight="24px"
        {...rest}
      />
      <Divider orientation="horizontal" mt={2} mb={hasMarginBottom ? 0 : 2} />
    </>
  );
}

type PanelTitleProps = {
  children: React.ReactNode;
};

export function PanelTitle({ children }: PanelTitleProps): React.ReactElement {
  return (
    <Heading size="sm" as="h3" textTransform="capitalize">
      {children}
    </Heading>
  );
}

type PanelActionButtonsProps = {
  children: React.ReactNode;
};

export function PanelActionButtons({
  children,
}: PanelActionButtonsProps): React.ReactElement {
  return <ButtonGroup>{children}</ButtonGroup>;
}

type PanelContentProps = {
  children: React.ReactNode;
};

const PanelContent = forwardRef<HTMLDivElement, PanelContentProps & BoxProps>(
  ({ children, ...rest }, ref) => {
    return (
      <Box
        ref={ref}
        fontSize="xs"
        paddingLeft={2}
        paddingBottom={2}
        overflow="auto"
        {...rest}
      >
        {children}
      </Box>
    );
  }
);

PanelContent.displayName = 'PanelContent';

export { PanelContent };
